import { asyncDebounce } from '@general/helpers';

/**
 * Given a query and session token, fetches place predictions using Google Maps Places API.
 * Uses asyncDebounce to limit the number of requests made to the API.
 *
 * @param {string} query - The text query for the place search.
 * @param {string} sessionToken - The Google Places API session token.
 * @param {number} time - Debounce delay in milliseconds (default: 500).
 * @returns {Promise<Array>} Array of place suggestion objects.
 */
export const getPlacesPredictions = asyncDebounce(async ({ input, sessionToken, ...args }) => {
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error('Google Maps Places library is not loaded');
    return [];
  }

  if (!input || !sessionToken) {
    console.error('Query or session token is missing');
    return [];
  }

  if (input.trim() === '' || input.length < 3) {
    console.warn('Query is too short or empty');
    return [];
  }

  try {
    const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: input.trim(),
      sessionToken: sessionToken,
      ...args,
    });

    return suggestions ?? [];
  } catch (error) {
    console.error('Error fetching place predictions:', error);
    return [];
  }
}, 500);

/**
 * Fetches details of a place by its ID using Google Maps Places API.
 * Returns the place details or null if an error occurs.
 * @param {Object} params - Parameters for fetching place details.
 * @param {string} params.id - The ID of the place to fetch details for.
 * @param {Array} [params.fields=['location']] - Fields to fetch for the place.
 * @returns {Promise<Object|null>} Place details or null if not found.
 * @throws {Error} If the Google Maps Places library is not loaded or if the place ID is missing.
 * @throws {Error} If an error occurs while fetching place details.
 */
export const getPlaceDetails = async ({ id, fields = ['location'] }) => {
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error('Google Maps Places library is not loaded');
    return null;
  }

  if (!id) {
    console.error('Place ID is missing');
    return null;
  }

  try {
    const place = new window.google.maps.places.Place({ id });
    await place.fetchFields({ fields });

    return place;
  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};
